import { FiFileText } from 'react-icons/fi';

export const nominalizationData = {
  courseId: 'nominalization',
  courseType: 'grammar',
  title: 'Nominalization',
  icon: FiFileText,
  overview: {
    impact: 'Transform verbs/adjectives into nouns for academic tone',
    focus: 'Creating noun forms for formal, concise writing',
    uses: 'Writing Task 2, academic style',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'What Is Nominalization?',
      content: `<p>Converting verbs/adjectives into noun forms to create more academic, formal sentences:</p>`,
      examples: [
        {
          label: 'Verbal',
          text: 'People communicate differently online.'
        },
        {
          label: 'Nominalized',
          text: 'Online communication differs significantly.'
        }
      ]
    },
    {
      title: 'Common Patterns',
      content: `<p>How to form nominalizations:</p>`,
      table: {
        headers: [
          'Verb/Adjective',
          'Noun Form',
          'Suffix'
        ],
        rows: [
          [
            'develop',
            'development',
            '-ment'
          ],
          [
            'educate',
            'education',
            '-tion/-sion'
          ],
          [
            'important',
            'importance',
            '-ance/-ence'
          ],
          [
            'accurate',
            'accuracy',
            '-acy'
          ],
          [
            'happy',
            'happiness',
            '-ness'
          ],
          [
            'analyze',
            'analysis',
            '-sis'
          ],
          [
            'grow',
            'growth',
            'irregular'
          ]
        ]
      }
    },
    {
      title: 'Using in IELTS Writing',
      content: `<p>Nominalization creates more sophisticated, concise sentences:</p>`,
      examples: [
        {
          label: 'Simple',
          text: 'Because people use technology too much, they become less social.'
        },
        {
          label: 'Nominalized',
          text: 'The excessive use of technology leads to a reduction in social interaction.'
        }
      ]
    }
  ],
  exercises: [
    {
      title: 'Nominalize the Sentence',
      type: 'writing',
      instructions: 'Rewrite using nominalization.',
      questions: [
        {
          text: 'The government decided to invest in education.',
          example: 'The government\'s decision to invest in education...',
          tip: 'decide → decision (verb → noun).'
        },
        {
          text: 'People consume too much and this affects the environment.',
          example: 'Excessive consumption has a significant environmental impact.',
          tip: 'consume → consumption, affect → impact.'
        }
      ]
    }
  ],
  tips: [
    'Nominalization = turning verbs/adjectives into nouns',
    'Creates formal, academic tone for Writing Task 2',
    'Common suffixes: -tion, -ment, -ness, -ity, -ance',
    'Don\'t overdo it — balance with some active verbs'
  ],
  dailyPractice: [
    'Practice nominalization concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Nominalization: Converting verbs/adjectives to noun forms for academic writing. Key suffixes: -tion, -ment, -ness, -ity. Creates Band 7+ formal register.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...nominalizationData,
      title: nominalizationData.title + ' (Foundations)',
      overview: { ...nominalizationData.overview, duration: '15-20 minutes' },
      sections: nominalizationData.sections.slice(0, Math.max(2, Math.ceil(nominalizationData.sections.length / 2))),
      exercises: nominalizationData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...nominalizationData,
      title: nominalizationData.title + ' (Advanced)',
      overview: { ...nominalizationData.overview, duration: '30-35 minutes' },
      sections: nominalizationData.sections,
      exercises: nominalizationData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(nominalizationData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return nominalizationData;
}
